<template>
  <section class="customers-toolbar app-filter-toolbar">
    <div class="toolbar-main-row app-filter-toolbar-row">
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="text.filters.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <multiselect
          v-model="filters.status"
          :options="options.status"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="statusFilterConfig?.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="statusFilterConfig?.placeholder"
          class="customer-status-inline app-filter-select app-select app-select--filters"
          @select="onSelect('status', $event)"
          @remove="onRemove('status')"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel('status', values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
          <template #noOptions>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="text.actions.toolbar.toggleFilters"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn customers-toolbar-search-btn"
          :title="text.actions.toolbar.search"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand">
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="customers-filters-grid customers-filters-grid--first-row">
          <div
            v-for="filter in firstRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
              <template #noOptions>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="customers-filters-grid customers-filters-grid--second-row">
          <div
            v-for="filter in secondRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
              <template #noOptions>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import customerCatalogsService from '@/services/customerCatalogs';
import areasService from '@/services/areas';
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
  withAllOption,
} from '@/utils/filterSelectors';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  mapToolbarOptions,
  resolveToolbarToggleState,
} from '@/composables/shared/useFilterToolbarShared';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomersToolbar',
  emits: ['filters-change', 'search-change', 'toggle-view', 'apply'],
  computed: {
    canFilterArea() {
      return typeof this.$can === 'function' && this.$can('customers.areas.assign');
    },
    statusFilterConfig() {
      return this.filterConfigByKey('status');
    },
    firstRowFilterConfigs() {
      const keys = ['type', 'sector', 'size'];

      return keys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    secondRowFilterConfigs() {
      const keys = ['origin', 'file'];

      if (this.canFilterArea) {
        keys.push('area');
      }

      return keys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
  },
  data() {
    const allStatus = { value: ALL_OPTION_VALUE, label: text.filters.statusAllLabel };
    const allSize = createToolbarAllOption();
    const allType = createToolbarAllOption();
    const allSector = createToolbarAllOption();
    const allOrigin = createToolbarAllOption();
    const allFile = createToolbarAllOption();
    const allArea = createToolbarAllOption();

    return {
      text,
      search: '',
      showFilters: false,
      options: {
        status: [
          allStatus,
          { value: 'active', label: text.filters.options.statusActive },
          { value: 'inactive', label: text.filters.options.statusInactive },
        ],
        size: [allSize],
        type: [allType],
        sector: [allSector],
        origin: [allOrigin],
        file: [
          allFile,
          { value: 'complete', label: text.filters.options.fileComplete },
          { value: 'incomplete', label: text.filters.options.fileIncomplete },
        ],
        area: [allArea],
      },
      filters: {
        status: [allStatus],
        size: [allSize],
        type: [allType],
        sector: [allSector],
        origin: [allOrigin],
        file: [allFile],
        area: [allArea],
      },
      filterConfigs: [
        {
          key: 'status',
          label: text.filters.fields.status.label,
          placeholder: text.filters.fields.status.placeholder,
          searchable: false,
        },
        {
          key: 'size',
          label: text.filters.fields.size.label,
          placeholder: text.filters.fields.size.placeholder,
          searchable: true,
        },
        {
          key: 'type',
          label: text.filters.fields.type.label,
          placeholder: text.filters.fields.type.placeholder,
          searchable: true,
        },
        {
          key: 'sector',
          label: text.filters.fields.sector.label,
          placeholder: text.filters.fields.sector.placeholder,
          searchable: true,
        },
        {
          key: 'origin',
          label: text.filters.fields.origin.label,
          placeholder: text.filters.fields.origin.placeholder,
          searchable: true,
        },
        {
          key: 'file',
          label: text.filters.fields.file.label,
          placeholder: text.filters.fields.file.placeholder,
          searchable: false,
        },
        {
          key: 'area',
          label: text.filters.fields.area.label,
          placeholder: text.filters.fields.area.placeholder,
          searchable: true,
        },
      ],
    };
  },
  mounted() {
    this.loadDynamicFilters();
  },
  methods: {
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    async loadDynamicFilters() {
      const requests = [
        customerCatalogsService.getCustomerSizes(),
        customerCatalogsService.getCustomerTypes(),
        customerCatalogsService.getCustomerSectors(),
        customerCatalogsService.getCustomerOrigins(),
      ];

      if (this.canFilterArea) {
        requests.push(areasService.index());
      }

      const [sizes, types, sectors, origins, areas] = await Promise.allSettled(requests);

      this.options.size = withAllOption(mapToolbarOptions(sizes.status === 'fulfilled' ? sizes.value?.data : []));
      this.options.type = withAllOption(mapToolbarOptions(types.status === 'fulfilled' ? types.value?.data : []));
      this.options.sector = withAllOption(mapToolbarOptions(sectors.status === 'fulfilled' ? sectors.value?.data : []));
      this.options.origin = withAllOption(mapToolbarOptions(origins.status === 'fulfilled' ? origins.value?.data : []));
      if (this.canFilterArea) {
        this.options.area = withAllOption(mapToolbarOptions(areas?.status === 'fulfilled' ? areas.value?.data : []));
      } else {
        this.options.area = [createToolbarAllOption()];
        this.filters.area = [this.options.area[0]];
      }

      this.ensureAllDefault('size');
      this.ensureAllDefault('type');
      this.ensureAllDefault('sector');
      this.ensureAllDefault('origin');
      this.ensureAllDefault('area');
    },
    ensureAllDefault(key) {
      this.filters[key] = ensureDefaultSelection(this.filters[key], this.options[key]?.[0]);
    },
    onSelect(key, option) {
      this.filters[key] = applySelectOption(this.filters[key], option, this.options[key]?.[0]);
    },
    onRemove(key) {
      this.filters[key] = applyRemoveOption(this.filters[key], this.options[key]?.[0]);
    },
    selectionLabel(key, values) {
      if (key === 'status') {
        const selected = Array.isArray(values) ? values : [];
        if (
          selected.length === 0
          || selected.some((option) => option?.value === ALL_OPTION_VALUE)
        ) {
          return buildToolbarSelectionLabel(values, text.filters.statusAllLabel);
        }
      }

      return buildToolbarSelectionLabel(values);
    },
    selectedValues(key) {
      return buildSelectedValues(this.filters[key]);
    },
    filterConfigByKey(key) {
      return this.filterConfigs.find((filter) => filter.key === key) || null;
    },
    emitFilters() {
      return {
        status: this.selectedValues('status'),
        size: this.selectedValues('size'),
        type: this.selectedValues('type'),
        sector: this.selectedValues('sector'),
        origin: this.selectedValues('origin'),
        file: this.selectedValues('file'),
        area: this.canFilterArea ? this.selectedValues('area') : [],
      };
    },
    emitApply() {
      const nextFilters = this.emitFilters();

      this.$emit('search-change', this.search);
      this.$emit('filters-change', nextFilters);
      this.$emit('apply', buildToolbarApplyPayload(this.search, nextFilters));
    },
  },
};
</script>

<style scoped>
.customer-status-inline {
  --app-filter-inline-width: 238px;
  width: 238px;
  min-width: 238px;
  flex: 0 0 238px;
  margin-top: 4px;
}

.customers-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.customers-toolbar-search-btn i {
  color: #ffffff;
}

.customers-toolbar-search-btn:hover,
.customers-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

.customers-filters-grid {
  display: grid;
  gap: 10px;
}

.customers-filters-grid--first-row,
.customers-filters-grid--second-row {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.customers-filters-grid--second-row {
  margin-top: 10px;
}

@media (max-width: 1200px) {
  .customers-filters-grid--first-row,
  .customers-filters-grid--second-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .customers-filters-grid--first-row,
  .customers-filters-grid--second-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
